import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { track } from '@/lib/analytics'
import { Spinner } from '@/components/ui/Spinner'

/**
 * Web access gate (mobile-only billing). Vore has no web checkout — Premium
 * is purchased in the mobile app; this screen says exactly that and offers a
 * refresh for a just-made purchase.
 */
export function Gate() {
  const { t } = useTranslation()
  const { user, access, recheckAccess, signOut, profileMissing, needsOnboarding } = useAuth()
  const navigate = useNavigate()
  const [refreshing, setRefreshing] = useState(false)

  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current) return
    if (profileMissing || needsOnboarding) {
      viewedRef.current = true
      track('web_no_account_shown')
    } else if (access === 'denied') {
      viewedRef.current = true
      track('paywall_viewed', { source: 'web_gate' })
    }
  }, [profileMissing, needsOnboarding, access])

  useEffect(() => {
    if (access === 'granted' && !profileMissing && !needsOnboarding) navigate('/log', { replace: true })
  }, [access, profileMissing, needsOnboarding, navigate])

  async function onRefresh() {
    setRefreshing(true)
    await recheckAccess()
    setRefreshing(false)
  }

  if (access === 'checking' && !profileMissing) {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <Spinner size={40} />
      </div>
    )
  }

  const footer = (
    <div className="mt-lg flex items-center justify-between gap-md text-caption text-text-muted">
      <span className="truncate" title={user?.email ?? ''}>
        {t('webapp.paywall.signedInAs', 'Signed in as')} {user?.email}
      </span>
      <button onClick={() => void signOut()} className="shrink-0 text-text-secondary hover:underline">
        {t('logout', 'Logout')}
      </button>
    </div>
  )

  if (profileMissing || needsOnboarding) {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <div className="w-full max-w-md animate-fade-in rounded-xl border border-border-light bg-surface p-xl text-center shadow-lg">
          <img src="/images/icons/icon-first-log.png" alt="" className="mx-auto mb-md h-20 w-20" />
          <h1 className="text-h3 font-bold text-text">Vore</h1>
          <p className="mt-sm text-body text-text-secondary">
            {needsOnboarding
              ? t('webapp.finishSetup', 'Finish setting up your plan in the Vore mobile app, then come back here.')
              : t('webapp.authNoSignup', 'New to Vore? Create your account in the mobile app, then sign in here.')}
          </p>
          {footer}
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-md animate-fade-in rounded-xl border border-border-light bg-surface p-xl text-center shadow-lg">
        <img src="/icon-192.png" alt="" className="mx-auto mb-md h-16 w-16 rounded-[18px]" />
        <h1 className="text-h2 font-bold text-text">{t('webapp.paywall.title', 'Vore on the web is a Premium feature')}</h1>
        <p className="mx-auto mt-sm max-w-md text-body text-text-secondary">
          {t('webapp.paywall.subtitleMobile', 'Subscribe to Vore Premium in the mobile app, then log meals, track your macros, weight and vitals from any computer — always in sync with your phone.')}
        </p>
        <p className="mt-md text-body-sm text-text-muted">{t('webapp.paywall.mobileHint', 'Open Vore on your phone → GET PRO.')}</p>
        <button
          onClick={() => void onRefresh()}
          disabled={refreshing}
          className="mt-lg w-full rounded-md bg-primary px-md py-md text-button font-semibold text-on-primary transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {refreshing ? t('webapp.paywall.checking', 'Checking…') : t('webapp.paywall.alreadySubscribed', 'Already subscribed? Refresh')}
        </button>
        {footer}
      </div>
    </div>
  )
}
